'use client';

import { c as _c } from "react/compiler-runtime";
import './content-feed-reactions.scss';
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { contentFeedReactionEmojis } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import { type UseContentFeedReactionsProps, useContentFeedReactions } from './content-feed-reactions.logic.ts';
export type { UseContentFeedReactionsProps } from './content-feed-reactions.logic.ts';
export interface ContentFeedReactionsProps extends UseContentFeedReactionsProps {
  /** Callback when the Comment button is clicked. When provided, a Comment button renders in the action bar. */
  onCommentClick?: () => void;
  /** Render in compact/inline mode for comment-level reactions */
  compact?: boolean;
}

/**
 * Reaction interaction bar for content feed cards.
 * Displays a centered "Like" button that opens an emoji picker drawer,
 * and a reaction summary row showing emoji counts.
 */
export const ContentFeedReactions: React.FC<ContentFeedReactionsProps> = props => {
  const $ = _c(51);
  const {
    onCommentClick,
    compact
  } = props;
  const {
    userReaction,
    totalCount,
    topReactions,
    isPickerOpen,
    canReact,
    justReactedEmoji,
    togglePicker,
    closePicker,
    handleSelectEmoji,
    getEmojiLabel,
    t
  } = useContentFeedReactions(props);
  const pickerRef = useRef(null);
  const likeButtonRef = useRef(null);
  const firstOptionRef = useRef(null);
  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const [isPickerClosing, setIsPickerClosing] = useState(false);
  let t0;
  if ($[0] !== isPickerOpen || $[1] !== isPickerVisible) {
    t0 = () => {
      if (isPickerOpen) {
        setIsPickerVisible(true);
        setIsPickerClosing(false);
      } else {
        if (isPickerVisible) {
          setIsPickerClosing(true);
          const timer = setTimeout(() => {
            setIsPickerVisible(false);
            setIsPickerClosing(false);
          }, 120);
          return () => clearTimeout(timer);
        }
      }
    };
    $[0] = isPickerOpen;
    $[1] = isPickerVisible;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== isPickerOpen) {
    t1 = [isPickerOpen];
    $[3] = isPickerOpen;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== closePicker || $[6] !== isPickerClosing || $[7] !== isPickerVisible) {
    t2 = () => {
      if (!isPickerVisible || isPickerClosing) {
        return;
      }
      const handleClickOutside = e => {
        if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
          closePicker();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t3 = [isPickerVisible, isPickerClosing, closePicker];
    $[5] = closePicker;
    $[6] = isPickerClosing;
    $[7] = isPickerVisible;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== isPickerOpen) {
    t4 = () => {
      if (isPickerOpen) {
        firstOptionRef.current?.focus();
      }
    };
    t5 = [isPickerOpen];
    $[10] = isPickerOpen;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== closePicker) {
    t6 = e_0 => {
      if (e_0.key !== "Escape") {
        return;
      }
      e_0.preventDefault();
      closePicker();
      likeButtonRef.current?.focus();
    };
    $[13] = closePicker;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleEscape = t6;
  const showCommentAction = !compact && !!onCommentClick;
  if (!canReact && !showCommentAction) {
    return null;
  }
  let t7;
  if ($[15] !== t || $[16] !== totalCount || $[17] !== userReaction) {
    t7 = () => {
      if (!userReaction) {
        return t("my:contentFeed.reactionsCount", {
          count: totalCount
        });
      }
      if (totalCount > 1) {
        return t("my:contentFeed.reactionsYouAndOthers", {
          count: totalCount - 1
        });
      }
      return t("my:contentFeed.reactionsYou");
    };
    $[15] = t;
    $[16] = totalCount;
    $[17] = userReaction;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const getReactionCountText = t7;
  let t8;
  if ($[19] !== compact) {
    t8 = cn("ContentFeedReactions flex flex-col", {
      "ContentFeedReactions--compact": compact
    });
    $[19] = compact;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== canReact || $[22] !== compact || $[23] !== getReactionCountText || $[24] !== justReactedEmoji || $[25] !== topReactions || $[26] !== totalCount) {
    t9 = canReact && !compact && totalCount > 0 && <div className="flex items-center gap-snug-lg px-comfy-lg pb-comfy-sm"><div className="ContentFeedReactions__emojis flex items-center">{topReactions.map(r => <span key={r.emoji} className={cn("ContentFeedReactions__emoji-badge", {
          "ContentFeedReactions__emoji-badge--just-added": r.emoji === justReactedEmoji
        })}>{r.emoji}</span>)}</div><span className="label-sm label-neutral-mild whitespace-nowrap">{getReactionCountText()}</span></div>;
    $[21] = canReact;
    $[22] = compact;
    $[23] = getReactionCountText;
    $[24] = justReactedEmoji;
    $[25] = topReactions;
    $[26] = totalCount;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== canReact || $[29] !== compact || $[30] !== getEmojiLabel || $[31] !== handleEscape || $[32] !== handleSelectEmoji || $[33] !== isPickerClosing || $[34] !== isPickerOpen || $[35] !== isPickerVisible || $[36] !== t || $[37] !== togglePicker || $[38] !== userReaction?.emoji) {
    t10 = canReact && <div className={cn("ContentFeedReactions__like-wrapper", {
      "flex flex-1 items-center justify-center": !compact
    })}><div className="ContentFeedReactions__like-anchor">{isPickerVisible && <div className={cn("ContentFeedReactions__picker", {
          "ContentFeedReactions__picker--closing": isPickerClosing
        })} role="group" aria-label={t("my:contentFeed.emojiPickerLabel")}>{contentFeedReactionEmojis.map((emoji, index) => <button key={emoji} ref={index === 0 ? firstOptionRef : undefined} type="button" className={cn("ContentFeedReactions__picker-item", {
            "ContentFeedReactions__picker-item--selected": userReaction?.emoji === emoji
          })} onClick={() => handleSelectEmoji(emoji)} onKeyDown={handleEscape} aria-label={getEmojiLabel(emoji)} aria-pressed={userReaction?.emoji === emoji}>{emoji}</button>)}</div>}<button ref={likeButtonRef} type="button" className={cn("ContentFeedReactions__action-btn flex items-center justify-center rounded-md", compact ? "gap-snug-md" : "gap-snug-lg px-comfy-md py-comfy-sm")} onClick={togglePicker} onKeyDown={handleEscape} aria-expanded={isPickerOpen}><ArkIcon iconName="thumbs-up" size={compact ? "16px" : "22px"} /><span className={compact ? "label-moderate label-md" : "label-potent label-lg"}>{t("my:contentFeed.like")}</span></button></div></div>;
    $[28] = canReact;
    $[29] = compact;
    $[30] = getEmojiLabel;
    $[31] = handleEscape;
    $[32] = handleSelectEmoji;
    $[33] = isPickerClosing;
    $[34] = isPickerOpen;
    $[35] = isPickerVisible;
    $[36] = t;
    $[37] = togglePicker;
    $[38] = userReaction?.emoji;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== compact || $[41] !== onCommentClick || $[42] !== t) {
    t11 = !compact && onCommentClick && <button type="button" className="ContentFeedReactions__action-btn flex flex-1 items-center justify-center gap-snug-lg px-comfy-md py-comfy-sm rounded-md" onClick={onCommentClick}><ArkIcon iconName="message-circle-01" size="22px" /><span className="label-potent label-lg">{t("my:contentFeed.comment")}</span></button>;
    $[40] = compact;
    $[41] = onCommentClick;
    $[42] = t;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== t10 || $[45] !== t11) {
    t12 = <div className="ContentFeedReactions__actions relative flex items-center px-comfy-lg gap-comfy-sm" ref={pickerRef}>{t10}{t11}</div>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t12 || $[48] !== t8 || $[49] !== t9) {
    t13 = <div className={t8}>{t9}{t12}</div>;
    $[47] = t12;
    $[48] = t8;
    $[49] = t9;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  return t13;
};